import React from "react";
import ReactDOM from "react-dom";
import { isSmallDeviceWidth, PaginationControl } from "component-library";
import { dataTestIds } from "data-testids";

import scrollManager from "shared/services/scrollManager";
import { Diff } from "shared/types/utilities";

import {
  PAGINATION_PAGE_RANGE_DESKTOP,
  PAGINATION_PAGE_RANGE_MOBILE,
} from "config/common";

import GlobalSpinner from "components/GlobalSpinner/GlobalSpinner";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";

export interface IWithPaginationTexts {
  resultStats: string;
}

export interface IWithPaginationProps extends IWithPaginationInjectedProps {
  pageNumber: number;
  updatePageNumber: (page: number | undefined) => void;
  itemsPerPage: number;
  itemsCount: number;
  isPageLoading: boolean;
  texts: IWithPaginationTexts;
  onPageChange?(pageNumber: number): void;
  "data-testid"?: string;
}

export interface IWithPaginationInjectedProps {
  renderPaginationResultStats(): React.JSX.Element;
  renderPagination(shouldScroll?: boolean): React.JSX.Element;
}

type HocProps<TProps extends object> = Diff<
  TProps,
  IWithPaginationInjectedProps
>;

export default function withPagination<TProps extends IWithPaginationProps>(
  WrappedComponent: React.ComponentType<React.PropsWithChildren<TProps>>
): React.ComponentType<React.PropsWithChildren<HocProps<TProps>>> {
  return class extends React.Component<
    React.PropsWithChildren<HocProps<TProps>>
  > {
    private wrappedComponentRef = React.createRef<React.Component>();

    public componentDidUpdate(
      prevProps: React.PropsWithChildren<HocProps<TProps>>
    ) {
      if (
        this.props.onPageChange &&
        this.props.pageNumber !== prevProps.pageNumber
      ) {
        this.props.onPageChange(this.props.pageNumber);
      }
    }

    public render() {
      if (this.props.isPageLoading) {
        return <GlobalSpinner isInner={true} />;
      }

      return (
        <WrappedComponent
          {...(this.props as TProps)}
          ref={this.wrappedComponentRef}
          renderPaginationResultStats={this.renderPaginationResultStats}
          renderPagination={this.renderPagination}
        />
      );
    }

    private renderPaginationResultStats = () => {
      return (
        <PaginationResultStats
          page={this.props.pageNumber}
          itemsPerPage={this.props.itemsPerPage}
          itemsTotal={this.props.itemsCount}
          textTemplate={this.props.texts.resultStats}
          data-testid={
            dataTestIds.nuJobsApp["BreakdownDetails.PaginationResultStats.text"]
          }
        />
      );
    };

    private renderPagination = (shouldScroll: boolean = false) => {
      return (
        <PaginationControl
          handlePageChange={
            shouldScroll
              ? this.handlePageChangeWithScroll
              : this.handlePageChange
          }
          pageRangeDisplayed={this.getPageRange()}
          pageCount={Math.ceil(this.props.itemsCount / this.props.itemsPerPage)}
          selectedPage={this.props.pageNumber}
          arePageBreaksMandatory={true}
          initialSelectedPage={this.props.pageNumber}
        />
      );
    };

    private handlePageChange = (page: number) => {
      this.props.updatePageNumber(page);
    };

    private handlePageChangeWithScroll = (page: number) => {
      scrollManager.scrollToElement(
        // eslint-disable-next-line react/no-find-dom-node
        ReactDOM.findDOMNode(this.wrappedComponentRef.current) as Element,
        0,
        0
      );
      this.handlePageChange(page);
    };

    private getPageRange = () =>
      isSmallDeviceWidth()
        ? PAGINATION_PAGE_RANGE_MOBILE
        : PAGINATION_PAGE_RANGE_DESKTOP;
  };
}
